/**
 * Infrastructure & Security Time Entry Templates
 * Vendor relations, cloud platforms, monitoring, backup, security,
 * network, email, and phone system activities.
 */

import type { TimeEntryTemplateData } from "./types";

export const INFRASTRUCTURE_TEMPLATES = [
  {
    id: "vendor-followup",
    name: "Vendor Follow-up",
    description: "Followed up with vendor regarding issue or inquiry",
    category: "Vendor Relations",
    fields: {
      description: "Followed up with vendor regarding {{vendor_name}} - {{issue_description}}",
      type: "billable",
      billable_rate: 100,
      tags: ["vendor", "follow-up", "communication"],
    },
  },
  {
    id: "cloud-setup-azure",
    name: "Azure Setup/Configuration",
    description: "Began setup or configuration work in Microsoft Azure",
    category: "Cloud Services",
    fields: {
      description: "Began setup in Azure - {{project_description}}",
      type: "billable",
      billable_rate: 125,
      tags: ["azure", "cloud", "setup", "configuration"],
    },
  },
  {
    id: "cloud-setup-aws",
    name: "AWS Setup/Configuration",
    description: "Began setup or configuration work in Amazon Web Services",
    category: "Cloud Services",
    fields: {
      description: "Began setup in AWS - {{project_description}}",
      type: "billable",
      billable_rate: 125,
      tags: ["aws", "cloud", "setup", "configuration"],
    },
  },
  {
    id: "ticket-investigation",
    name: "Ticket Investigation",
    description: "Investigated and analyzed ticket issue",
    category: "Support",
    fields: {
      description: "Investigated ticket issue - {{issue_summary}}",
      type: "billable",
      billable_rate: 100,
      tags: ["ticket", "investigation", "analysis", "troubleshooting"],
    },
  },
  {
    id: "system-monitoring",
    name: "System Monitoring",
    description: "Monitored systems and reviewed alerts",
    category: "Monitoring",
    fields: {
      description: "System monitoring and alert review for {{system_name}}",
      type: "billable",
      billable_rate: 100,
      tags: ["monitoring", "alerts", "systems", "review"],
    },
  },
  {
    id: "backup-verification",
    name: "Backup Verification",
    description: "Verified backup systems and tested restore procedures",
    category: "Backup & Recovery",
    fields: {
      description: "Backup verification and restore testing for {{backup_system}}",
      type: "billable",
      billable_rate: 100,
      tags: ["backup", "verification", "testing", "restore"],
    },
  },
  {
    id: "security-patch",
    name: "Security Patching",
    description: "Applied security patches and updates",
    category: "Security",
    fields: {
      description: "Applied security patches to {{system_name}} - {{patch_description}}",
      type: "billable",
      billable_rate: 125,
      tags: ["security", "patching", "updates", "maintenance"],
    },
  },
  {
    id: "network-troubleshooting",
    name: "Network Troubleshooting",
    description: "Troubleshot network connectivity issues",
    category: "Network",
    fields: {
      description: "Network troubleshooting for {{network_issue}}",
      type: "billable",
      billable_rate: 100,
      tags: ["network", "troubleshooting", "connectivity", "diagnostics"],
    },
  },
  {
    id: "email-configuration",
    name: "Email Configuration",
    description: "Configured email settings and resolved email issues",
    category: "Email Systems",
    fields: {
      description: "Email configuration and troubleshooting for {{user_name}}",
      type: "billable",
      billable_rate: 100,
      tags: ["email", "configuration", "troubleshooting", "setup"],
    },
  },
  {
    id: "phone-system-config",
    name: "Phone System Configuration",
    description: "Configured phone system settings and extensions",
    category: "Phone Systems",
    fields: {
      description: "Phone system configuration for {{user_name}} - {{extension_number}}",
      type: "billable",
      billable_rate: 100,
      tags: ["phone", "voip", "configuration", "extensions"],
    },
  },
  {
    id: "incident-response",
    name: "Incident Response",
    description: "Responded to security or system incidents",
    category: "Security",
    fields: {
      description: "Incident response for {{incident_type}} - {{severity}}",
      type: "billable",
      billable_rate: 150,
      tags: ["incident", "response", "security", "emergency"],
    },
  },
  {
    id: "compliance-review",
    name: "Compliance Review",
    description: "Reviewed systems for compliance requirements",
    category: "Compliance",
    fields: {
      description: "Compliance review for {{compliance_standard}} - {{scope}}",
      type: "billable",
      billable_rate: 150,
      tags: ["compliance", "review", "audit", "standards"],
    },
  },
] as const satisfies readonly TimeEntryTemplateData[];
